import { createAction, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  FeatureFlagDto,
  FeatureFlagPayload,
} from "../../types/featureFlag.definition";

interface FeatureFlagState {
  rules: FeatureFlagDto[];
  loading: boolean;
  saving: boolean;
  deleting: boolean;
  error: string | null;
}

const initialState: FeatureFlagState = {
  rules: [],
  loading: false,
  saving: false,
  deleting: false,
  error: null,
};

// ── Actions ───────────────────────────────────────────────────────────────

export const fetchFeatureFlagsRequest = createAction(
  "featureFlags/fetchFeatureFlagsRequest"
);

export const fetchFeatureFlagsSuccess = createAction<FeatureFlagDto[]>(
  "featureFlags/fetchFeatureFlagsSuccess"
);

export const fetchFeatureFlagsFailure = createAction<string>(
  "featureFlags/fetchFeatureFlagsFailure"
);

export const createFeatureFlagRequest = createAction<{
  data: FeatureFlagPayload;
  onSuccess?: (rule: FeatureFlagDto) => void;
  onError?: (code: string) => void;
}>("featureFlags/createFeatureFlagRequest");

export const createFeatureFlagSuccess = createAction<FeatureFlagDto>(
  "featureFlags/createFeatureFlagSuccess"
);
export const createFeatureFlagFailure = createAction<string>(
  "featureFlags/createFeatureFlagFailure"
);

export const updateFeatureFlagRequest = createAction<{
  id: number;
  data: FeatureFlagPayload;
  onSuccess?: (rule: FeatureFlagDto) => void;
  onError?: (code: string) => void;
}>("featureFlags/updateFeatureFlagRequest");

export const updateFeatureFlagSuccess = createAction<FeatureFlagDto>(
  "featureFlags/updateFeatureFlagSuccess"
);
export const updateFeatureFlagFailure = createAction<string>(
  "featureFlags/updateFeatureFlagFailure"
);

export const deleteFeatureFlagRequest = createAction<{
  id: number;
  onSuccess?: () => void;
  onError?: (code: string) => void;
}>("featureFlags/deleteFeatureFlagRequest");

export const deleteFeatureFlagSuccess = createAction<number>(
  "featureFlags/deleteFeatureFlagSuccess"
);
export const deleteFeatureFlagFailure = createAction<string>(
  "featureFlags/deleteFeatureFlagFailure"
);

// ── Slice ─────────────────────────────────────────────────────────────────

const featureFlagSlice = createSlice({
  name: "featureFlags",
  initialState,
  reducers: {
    clearError: state => {
      state.error = null;
    },
  },
  extraReducers: builder => {
    // Fetch
    builder
      .addCase(fetchFeatureFlagsRequest, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchFeatureFlagsSuccess,
        (state, action: PayloadAction<FeatureFlagDto[]>) => {
          state.loading = false;
          state.rules = action.payload;
          state.error = null;
        }
      )
      .addCase(
        fetchFeatureFlagsFailure,
        (state, action: PayloadAction<string>) => {
          state.loading = false;
          state.error = action.payload;
        }
      );

    // Create
    builder
      .addCase(createFeatureFlagRequest, state => {
        state.saving = true;
        state.error = null;
      })
      .addCase(
        createFeatureFlagSuccess,
        (state, action: PayloadAction<FeatureFlagDto>) => {
          state.saving = false;
          state.error = null;
          state.rules = [action.payload, ...state.rules];
        }
      )
      .addCase(
        createFeatureFlagFailure,
        (state, action: PayloadAction<string>) => {
          state.saving = false;
          state.error = action.payload;
        }
      );

    // Update
    builder
      .addCase(updateFeatureFlagRequest, state => {
        state.saving = true;
        state.error = null;
      })
      .addCase(
        updateFeatureFlagSuccess,
        (state, action: PayloadAction<FeatureFlagDto>) => {
          state.saving = false;
          state.error = null;
          state.rules = state.rules.map(r =>
            r.id === action.payload.id ? action.payload : r
          );
        }
      )
      .addCase(
        updateFeatureFlagFailure,
        (state, action: PayloadAction<string>) => {
          state.saving = false;
          state.error = action.payload;
        }
      );

    // Delete
    builder
      .addCase(deleteFeatureFlagRequest, state => {
        state.deleting = true;
        state.error = null;
      })
      .addCase(
        deleteFeatureFlagSuccess,
        (state, action: PayloadAction<number>) => {
          state.deleting = false;
          state.rules = state.rules.filter(r => r.id !== action.payload);
          state.error = null;
        }
      )
      .addCase(
        deleteFeatureFlagFailure,
        (state, action: PayloadAction<string>) => {
          state.deleting = false;
          state.error = action.payload;
        }
      );
  },
});

export const { clearError } = featureFlagSlice.actions;

// ── Selectors ─────────────────────────────────────────────────────────────

export const selectFeatureFlagRules = (state: {
  featureFlags: FeatureFlagState;
}) => state.featureFlags.rules;
export const selectFeatureFlagLoading = (state: {
  featureFlags: FeatureFlagState;
}) => state.featureFlags.loading;
export const selectFeatureFlagSaving = (state: {
  featureFlags: FeatureFlagState;
}) => state.featureFlags.saving;
export const selectFeatureFlagDeleting = (state: {
  featureFlags: FeatureFlagState;
}) => state.featureFlags.deleting;
export const selectFeatureFlagError = (state: {
  featureFlags: FeatureFlagState;
}) => state.featureFlags.error;

export default featureFlagSlice.reducer;
